import React, {useEffect, useMemo, useState} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {ListMessage} from 'app/components/general/ListMessage';
import {useSearchResults} from 'app/search/api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {LoadingList} from './LoadingList';

import type {
  SearchSerializedType,
  TaskAssignableContentTypes,
} from 'sweet-types';

interface ListAndPageSharedProps {
  search: string;
  selected: {
    contentType: TaskAssignableContentTypes;
    objectId: number;
  } | null;
  onSelect: (
    doc: {
      contentType: TaskAssignableContentTypes;
      objectId: number;
    } | null
  ) => unknown;
  onIsFetchingChange: (isFetching: boolean) => unknown;
  isLoadingDocDetails: boolean;
}

export function InfiniteSearchResultsList({
  search,
  ...props
}: ListAndPageSharedProps) {
  const [pagination, setPagination] = useState({search, numPages: 1});

  useEffect(() => {
    /* when filters are changed, we need to:
      - reset number of pages to 1
      - update search copy
    */
    setPagination({
      numPages: 1,
      search,
    });
  }, [search]);

  // number of pages to load
  const pages = useMemo(
    () => Array.from({length: pagination.numPages}, (_, i) => i + 1),
    [pagination.numPages]
  );

  return (
    <StyledListDiv>
      {pages.map((i) => (
        <SearchResultsPage
          search={pagination.search}
          key={i}
          page={i}
          loadNext={(inc = 1) =>
            setPagination((state) => ({
              ...state,
              numPages: state.numPages + inc,
            }))
          }
          isLastPage={i === pages[pages.length - 1]}
          {...props}
        />
      ))}
    </StyledListDiv>
  );
}

interface SearchResultsPageProps extends ListAndPageSharedProps {
  page: number;
  isLastPage: boolean;
  loadNext: (inc?: number) => unknown;
}

function SearchResultsPage({
  selected,
  onSelect,
  page,
  search,
  loadNext,
  isLastPage,
  isLoadingDocDetails,
  onIsFetchingChange,
}: SearchResultsPageProps) {
  const {
    data: searchResults,
    isFetching,
    isLoading,
    error,
  } = useSearchResults(
    {
      page: page.toString(),
      page_size: '20',
      search,
      type: 'procedure,proceduregroup',
      ordering: 'name',
    },
    // only refetch if the results have been cached for 30 seconds
    {refetchOnMountOrArgChange: 30}
  );

  React.useEffect(() => {
    if (page > 1 && error != null && searchResults == null && !isLoading) {
      // if we have an error, show 'load more' again
      loadNext(-1);
    }
  }, [error?.toString(), isLoading]);

  React.useEffect(() => {
    if (page === 1) {
      onIsFetchingChange(isFetching);
    }
  }, [isFetching]);

  return (
    <>
      {searchResults == null && isFetching ? (
        page === 1 ? (
          <LoadingList />
        ) : (
          <LoadMoreButton isLoading />
        )
      ) : searchResults != null ? (
        searchResults.count === 0 ? (
          <ListMessage>
            {search
              ? gt('No matching procedures or processes')
              : gt('No procedures or processes found')}
          </ListMessage>
        ) : (
          <>
            {searchResults.results.map(
              (result: SearchSerializedType<TaskAssignableContentTypes>) => {
                const isSelected =
                  selected != null &&
                  result.object_id === selected.objectId &&
                  result.content_type === selected.contentType;
                return (
                  <SearchResultItem
                    selected={isSelected}
                    onToggle={() =>
                      onSelect(
                        isSelected
                          ? null
                          : {
                              contentType: result.content_type,
                              objectId: result.object_id,
                            }
                      )
                    }
                    result={result}
                    key={result.html_url}
                    showLoadingIndicator={isSelected && isLoadingDocDetails}
                  />
                );
              }
            )}
            {isLastPage && searchResults.next != null ? (
              <LoadMoreButton onClick={() => loadNext()} />
            ) : null}
          </>
        )
      ) : page === 1 ? (
        <ListMessage subText={gt('Please try again later')}>
          <div>{gt('There was an error loading the documents')}</div>
        </ListMessage>
      ) : (
        <LoadMoreButton isLoading />
      )}
    </>
  );
}

function SearchResultItem({
  result,
  selected,
  onToggle,
  showLoadingIndicator,
}: {
  selected: boolean;
  onToggle: () => unknown;
  result: SearchSerializedType<TaskAssignableContentTypes>;
  showLoadingIndicator?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className={cs('list-group-item list-group-item-action d-flex', {
        active: selected,
      })}
      aria-current={selected ? 'true' : 'false'}
      onClick={onToggle}
      style={{fontSize: '0.95rem'}}
    >
      <i
        className={cs(
          'fa-fw mr-2 mt-1 flex-shrink-0 align-self-start',
          showLoadingIndicator
            ? 'fa fa-circle-notch fa-spin'
            : getContentType(result.content_type).icon
        )}
      />
      <span className="flex-grow-1">{result.name}</span>
    </button>
  );
}

function LoadMoreButton({
  isLoading = false,
  onClick,
}: {
  isLoading?: boolean;
  onClick?: () => unknown;
}) {
  return (
    <button
      type="button"
      className={cs(
        'justify-content-center list-group-item list-group-item-action d-flex align-items-center',
        {'text-primary': !isLoading}
      )}
      onClick={() => onClick?.()}
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <i className="fa fa-circle-notch fa-spin mr-1" /> {gt('Loading...')}
        </>
      ) : (
        gt('Load more')
      )}
    </button>
  );
}

const StyledListDiv = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 24rem);

  @media (min-width: 576px) {
    max-height: calc(100vh - 26rem);
  }

  & > :first-child {
    border-top: none;
  }
  & > :last-child {
    border-bottom: none;
  }
`;
